/**
 * API functions for Limits BO module.
 * Alineado con BackofficeLimitRuleController — /api/v1/bo/limit-rules.
 *
 * @taskId T478
 * @see functional/Specs/specs_cycle_4.md HU-28-FE
 */

import { authFetch, normalizeApiError } from '@flexibilitypay/shared';
import type {
  AssignMerchantPayload,
  CreateLimitRulePayload,
  LimitAssignment,
  LimitRule,
  LimitRuleListParams,
  LimitRuleListResponse,
  MerchantAssignmentSummary,
  UpdateLimitRulePayload,
} from '../features/limits/types/limit.types';

const BASE_PATH = '/api/v1/bo/limit-rules';

/**
 * GET /api/v1/bo/limit-rules — Lista reglas de límites.
 */
export async function getLimitRules(params: LimitRuleListParams): Promise<LimitRuleListResponse> {
  const searchParams = new URLSearchParams();

  if (params.page !== undefined) searchParams.set('page', String(params.page));
  if (params.size !== undefined) searchParams.set('size', String(params.size));
  if (params.active !== undefined) searchParams.set('active', String(params.active));
  if (params.operationType) searchParams.set('operationType', params.operationType);

  const res = await authFetch(`${BASE_PATH}?${searchParams.toString()}`);
  if (!res.ok) throw await normalizeApiError(res);
  return res.json();
}

/**
 * GET /api/v1/bo/limit-rules/{id} — Detalle de regla.
 */
export async function getLimitRule(id: string): Promise<LimitRule> {
  const res = await authFetch(`${BASE_PATH}/${id}`);
  if (!res.ok) throw await normalizeApiError(res);
  return res.json();
}

/**
 * POST /api/v1/bo/limit-rules — Crear nueva regla.
 */
export async function createLimitRule(payload: CreateLimitRulePayload): Promise<LimitRule> {
  const res = await authFetch(BASE_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await normalizeApiError(res);
  return res.json();
}

/**
 * PATCH /api/v1/bo/limit-rules/{id} — Actualizar regla (incluye toggle active).
 */
export async function updateLimitRule(id: string, payload: UpdateLimitRulePayload): Promise<LimitRule> {
  const res = await authFetch(`${BASE_PATH}/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await normalizeApiError(res);
  return res.json();
}

/**
 * POST /api/v1/bo/limit-rules/{ruleId}/assignments — Asignar merchant a regla.
 */
export async function assignMerchantToRule(
  ruleId: string,
  payload: AssignMerchantPayload,
): Promise<LimitAssignment> {
  const res = await authFetch(`${BASE_PATH}/${ruleId}/assignments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await normalizeApiError(res);
  return res.json();
}

/**
 * DELETE /api/v1/bo/limit-rules/{ruleId}/assignments/{assignmentId} — Quitar asignación.
 */
export async function unassignMerchantFromRule(
  ruleId: string,
  assignmentId: string,
): Promise<void> {
  const res = await authFetch(`${BASE_PATH}/${ruleId}/assignments/${assignmentId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw await normalizeApiError(res);
}

/**
 * GET /api/v1/bo/limit-rules/{ruleId}/merchants — Lista merchants con estado de asignación.
 */
export async function getLimitRuleMerchants(
  ruleId: string,
  search?: string,
): Promise<MerchantAssignmentSummary[]> {
  const searchParams = new URLSearchParams();
  if (search) searchParams.set('search', search);
  const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const res = await authFetch(`${BASE_PATH}/${ruleId}/merchants${query}`);
  if (!res.ok) throw await normalizeApiError(res);
  return res.json();
}

/**
 * PATCH /api/v1/bo/limit-rules/{id}/toggle — Alterna el estado activo/inactivo de la regla.
 */
export async function toggleLimitRuleStatus(id: string): Promise<LimitRule> {
  const res = await authFetch(`${BASE_PATH}/${id}/toggle`, {
    method: 'PATCH',
  });
  if (!res.ok) throw await normalizeApiError(res);
  return res.json();
}
